import { ContractDocumentsCard } from "@/components/pages/client/contractDetails/contract-documents-card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { useQuoteContext } from "@/contexts/quote-context";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  cadastrarClienteContrato,
  getSalvarContratoEcommerceUrl,
  registrarContratoAssinado,
} from "@/services/flowQuote";
import { formatMoneyBr } from "@/utils/format";
import { openInNewTab } from "@/utils/url-helpers";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Circle,
  Download,
  ExternalLink,
  FileSignature,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import CardContactSpecialist from "../card-contact-specialist";
import AddressFields from "../payment/address-fields";
import { ClientInfoData, clientInfoSchema } from "../payment/payment-types";
import QuoteCardResume from "../quote-card-resume";
import QuoteCardResumeMobile from "../quote-card-resume-mobile";

interface ClientInfoStepProps {
  onNext: () => void;
  onBack: () => void;
}

const CONTRACT_BASE_URL = `${import.meta.env.VITE_API_URL}/anexos/contratos`;

function getContractUrl(
  ctc_codigo: string | null | undefined,
  cli_codigo: string | null | undefined,
): string | null {
  if (!ctc_codigo || !cli_codigo) return null;
  return `${CONTRACT_BASE_URL}/contrato_${ctc_codigo}_${cli_codigo}.pdf`;
}

export function ClientInfoStep({ onNext, onBack }: ClientInfoStepProps) {
  const {
    cotacao,
    clientInfoData,
    saveClientInfo,
    fetchCotacaoById,
    quotePrice,
  } = useQuoteContext();

  const isMobile = useMediaQuery("(max-width: 767px)");
  const [isOrderSummaryExpanded, setIsOrderSummaryExpanded] = useState(false);
  const [contractGenerated, setContractGenerated] = useState(false);
  const [contractAccepted, setContractAccepted] = useState(false);
  const [contractCliCodigo, setContractCliCodigo] = useState<string | null>(
    null,
  );
  const [contractLoading, setContractLoading] = useState(false);
  const [contractLoadingStep, setContractLoadingStep] = useState("");
  const [contractSnapshot, setContractSnapshot] =
    useState<ClientInfoData | null>(null);
  const [contractSigned, setContractSigned] = useState(false);

  const clienteData = cotacao?.cliente;

  const form = useForm<ClientInfoData>({
    resolver: zodResolver(clientInfoSchema),
    defaultValues: {
      personalInfo: {
        name:
          clientInfoData?.personalInfo.name ??
          clienteData?.cli_razao_social ??
          "",
        cpf: clientInfoData?.personalInfo.cpf ?? clienteData?.cli_cnpj ?? "",
      },
      address: {
        cep: clientInfoData?.address.cep ?? clienteData?.cle_cep ?? "",
        logradouro:
          clientInfoData?.address.logradouro ?? clienteData?.cle_endereco ?? "",
        numero: clientInfoData?.address.numero ?? clienteData?.cle_numero ?? "",
        complemento:
          clientInfoData?.address.complemento ??
          clienteData?.cle_complemento ??
          "",
        bairro: clientInfoData?.address.bairro ?? clienteData?.cle_bairro ?? "",
        cidade: clientInfoData?.address.cidade ?? "",
        estado: clientInfoData?.address.estado ?? "",
        mun_codigo:
          clientInfoData?.address.mun_codigo ?? clienteData?.mun_codigo ?? "",
        cep_pesquisado:
          clientInfoData?.address.cep_pesquisado ?? clienteData?.cle_cep ?? "",
      },
    },
    mode: "onSubmit",
  });

  const handleSubmit = () => {
    if (!contractAccepted) {
      toast({
        title: "Contrato não aceito",
        description:
          "Gere e aceite o contrato antes de continuar para o pagamento.",
        variant: "destructive",
      });
      return;
    }

    if (!contractSnapshot) {
      toast({
        title: "Contrato não gerado",
        description: "Gere o contrato antes de continuar para o pagamento.",
        variant: "destructive",
      });
      return;
    }

    saveClientInfo(contractSnapshot);
    onNext();
  };

  const handleGenerateContract = async () => {
    const isValid = await form.trigger([
      "personalInfo.name",
      "personalInfo.cpf",
      "address.cep",
      "address.logradouro",
      "address.numero",
      "address.bairro",
      "address.mun_codigo",
    ]);
    if (!isValid) {
      toast({
        title: "Dados incompletos",
        description:
          "Preencha todos os dados do titular antes de gerar o contrato.",
        variant: "destructive",
      });
      return;
    }

    const cepAtual = form.getValues("address.cep");
    const cepPesquisado = form.getValues("address.cep_pesquisado");
    if (!cepPesquisado || cepAtual !== cepPesquisado) {
      toast({
        title: "CEP não pesquisado",
        description: "Clique em 'Pesquisar' antes de gerar o contrato.",
        variant: "destructive",
      });
      return;
    }

    const sleep = (ms: number) =>
      new Promise((resolve) => setTimeout(resolve, ms));

    setContractLoading(true);
    try {
      const values = form.getValues();

      setContractLoadingStep("Cadastrando cliente...");
      const result = await cadastrarClienteContrato({
        cve_codigo: cotacao!.cve_codigo,
        ctc_codigo: cotacao!.ctc_codigo,
        cpf: values.personalInfo.cpf,
        nome: values.personalInfo.name,
        email: cotacao!.ctc_email,
        telefone: cotacao!.ctc_telefone_1,
        endereco: values.address.logradouro,
        numero_endereco: values.address.numero,
        complemento: values.address.complemento ?? "",
        bairro: values.address.bairro,
        cep: values.address.cep,
        mun_codigo: values.address.mun_codigo,
      });

      if (!result.sucesso) {
        toast({
          title: "Erro ao gerar contrato",
          description: "Não foi possível gerar o contrato. Tente novamente.",
          variant: "destructive",
        });
        return;
      }

      setContractLoadingStep("Preparando contrato...");
      await sleep(3000);

      setContractLoadingStep("Gerando PDF...");
      const genUrl = getSalvarContratoEcommerceUrl(
        cotacao!.cve_codigo,
        cotacao!.ctc_codigo,
        result.cli_codigo,
      );
      const genIframe = document.createElement("iframe");
      genIframe.src = genUrl;
      genIframe.style.display = "none";
      document.body.appendChild(genIframe);

      setContractLoadingStep("Carregando documento...");
      await sleep(5000);

      document.body.removeChild(genIframe);

      setContractCliCodigo(result.cli_codigo);
      setContractSnapshot(values);
      setContractGenerated(true);
      setContractAccepted(false);
    } catch {
      toast({
        title: "Erro ao gerar contrato",
        description: "Não foi possível gerar o contrato. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setContractLoading(false);
    }
  };

  const handleRegenerate = () => {
    setContractGenerated(false);
    setContractAccepted(false);
    setContractCliCodigo(null);
    setContractSnapshot(null);
    setContractLoadingStep("");
  };

  if (!cotacao) return null;

  const alreadySigned = !!cotacao.cve_data_contrato || contractSigned;
  const formValues = form.getValues();
  const contractPdfUrl = alreadySigned
    ? getContractUrl(
        cotacao.ctc_codigo,
        cotacao.cliente?.cli_codigo ?? cotacao.cli_codigo_contratante,
      )
    : getContractUrl(cotacao.ctc_codigo, contractCliCodigo);

  const handleGoToPaymentSigned = () => {
    if (cotacao.cliente) {
      saveClientInfo({
        personalInfo: {
          name: cotacao.cliente.cli_razao_social,
          cpf: cotacao.cliente.cli_cnpj,
        },
        address: {
          cep: cotacao.cliente.cle_cep,
          logradouro: cotacao.cliente.cle_endereco,
          numero: cotacao.cliente.cle_numero,
          complemento: cotacao.cliente.cle_complemento ?? "",
          bairro: cotacao.cliente.cle_bairro,
          cidade: "",
          estado: "",
          mun_codigo: cotacao.cliente.mun_codigo,
          cep_pesquisado: cotacao.cliente.cle_cep,
        },
      });
    }
    onNext();
  };

  return (
    <div className="">
      <div className="grid grid-cols-1 md:grid-cols-3 md:gap-6 gap-4 mb-[50px]">
        {/* Coluna principal */}
        <div className="md:col-span-2">
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Form {...(form as any)}>
            <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
              {/* Card: Dados pessoais */}
              <div className="bg-n-bg-2 md:p-6 p-4 rounded-[20px] border shadow-sm md:space-y-6 space-y-2">
                <h3 className="text-xl font-medium text-n-primary">
                  Dados do titular:
                </h3>

                {alreadySigned ? (
                  <>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3 text-sm">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Nome
                          </p>
                          <p className="font-medium text-gray-800">
                            {cotacao.cliente?.cli_razao_social || "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            CPF / CNPJ
                          </p>
                          <p className="font-medium text-gray-800">
                            {cotacao.cliente?.cli_cnpj || "—"}
                          </p>
                        </div>
                        <div className="sm:col-span-2">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Endereço de cobrança
                          </p>
                          <p className="font-medium text-gray-800">
                            {cotacao.cliente
                              ? `${cotacao.cliente.cle_endereco}, ${cotacao.cliente.cle_numero}${cotacao.cliente.cle_complemento ? ` – ${cotacao.cliente.cle_complemento}` : ""}, ${cotacao.cliente.cle_bairro} – CEP ${cotacao.cliente.cle_cep}`
                              : "—"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-3">
                      <FormField
                        control={form.control}
                        name="personalInfo.name"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input placeholder="Nome completo" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="personalInfo.cpf"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input placeholder="CPF / CNPJ" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <h4 className="font-medium text-n-primary mb-4">
                        Endereço de cobrança:
                      </h4>
                      <AddressFields
                        coletaCep={cotacao.cve_coleta_cep}
                        coletaEndereco={cotacao.cve_coleta_endereco}
                        coletaNumero={cotacao.cve_coleta_numero}
                        coletaComplemento={cotacao.cve_coleta_complemento}
                        coletaBairro={cotacao.cve_coleta_bairro}
                        coletaCidade={cotacao.mun_descricao_coleta}
                        entregaCep={cotacao.cve_entrega_cep}
                        entregaEndereco={cotacao.cve_entrega_endereco}
                        entregaNumero={cotacao.cve_entrega_numero}
                        entregaComplemento={cotacao.cve_entrega_complemento}
                        entregaBairro={cotacao.cve_entrega_bairro}
                        entregaCidade={cotacao.mun_descricao_entrega}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Card: Documentos */}
              <div className="bg-n-bg-2 md:p-6 p-4 rounded-[20px] border shadow-sm md:mt-4 mt-2">
                <ContractDocumentsCard
                  cotacao={cotacao}
                  simplified
                  onUpdate={async (cve_codigo) => {
                    await fetchCotacaoById(cve_codigo);
                  }}
                />
              </div>

              {/* Card: Contrato */}
              <div className="bg-n-bg-2 md:p-6 p-4 rounded-[20px] border shadow-sm md:mt-4 mt-2 md:space-y-5 space-y-2">
                <h3 className="text-xl font-medium text-n-primary">
                  Contrato:
                </h3>

                {alreadySigned ? (
                  <div className="space-y-4">
                    {/* Banner assinado */}
                    <div className="flex items-center md:gap-3 gap-2 rounded-xl border border-n-green-status bg-n-success-light p-4">
                      <CheckCircle2 className="w-5 h-5 text-n-success flex-shrink-0 " />
                      <p className="text-sm font-medium text-n-primary">
                        Contrato assinado
                        {cotacao.cve_data_contrato
                          ? ` em ${new Date(cotacao.cve_data_contrato).toLocaleDateString("pt-BR")}`
                          : ""}
                        .
                      </p>
                    </div>

                    {/* PDF viewer */}
                    <div className="border rounded-xl overflow-hidden shadow-sm">
                      <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b">
                        <span className="text-sm font-medium text-gray-700">
                          Contrato de Prestação de Serviços
                        </span>
                        <div className="flex items-end md:items-center md:gap-2 flex-col md:flex-row ">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 gap-1.5 text-xs"
                            disabled={!contractPdfUrl}
                            onClick={() =>
                              openInNewTab(
                                contractPdfUrl!,
                                "Não foi possível abrir o contrato.",
                              )
                            }
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Abrir em nova aba
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 gap-1.5 text-xs"
                            disabled={!contractPdfUrl}
                            onClick={async () => {
                              if (!contractPdfUrl) return;
                              try {
                                const res = await fetch(contractPdfUrl);
                                const blob = await res.blob();
                                const blobUrl = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = blobUrl;
                                a.download = `contrato_${cotacao.ctc_codigo}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(blobUrl);
                              } catch {
                                openInNewTab(
                                  contractPdfUrl,
                                  "Não foi possível baixar o contrato.",
                                );
                              }
                            }}
                          >
                            <Download className="w-3.5 h-3.5" />
                            Baixar PDF
                          </Button>
                        </div>
                      </div>
                      {contractPdfUrl ? (
                        <iframe
                          src={contractPdfUrl}
                          className="w-full h-[500px] bg-gray-50"
                          title="Contrato de prestação de serviços"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-[500px] bg-gray-50 text-sm text-gray-500">
                          Contrato indisponível para visualização.
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Aviso + resumo de dados */}
                    <div className="flex items-start gap-3 rounded-xl border border-yellow-300 bg-yellow-50 p-4">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div className="space-y-1 text-sm">
                        <p className="font-medium text-yellow-800">
                          Confira seus dados antes de gerar o contrato.
                        </p>
                        <p className="text-yellow-700">
                          Após aceitar o contrato, os dados abaixo não poderão
                          ser alterados sem gerar uma nova versão.
                        </p>
                      </div>
                    </div>

                    {/* Dados compilados */}
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3 text-sm">
                      <p className="font-semibold text-n-primary text-base">
                        Dados do contrato:
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Nome
                          </p>
                          <p className="font-medium text-gray-800">
                            {formValues.personalInfo?.name || "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            CPF / CNPJ
                          </p>
                          <p className="font-medium text-gray-800">
                            {formValues.personalInfo?.cpf || "—"}
                          </p>
                        </div>
                        <div className="sm:col-span-2">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Endereço de cobrança
                          </p>
                          <p className="font-medium text-gray-800">
                            {formValues.address?.logradouro
                              ? `${formValues.address.logradouro}, ${formValues.address.numero}${formValues.address.complemento ? ` – ${formValues.address.complemento}` : ""}, ${formValues.address.bairro}${formValues.address.cidade ? ` – ${formValues.address.cidade}` : ""}${formValues.address.estado ? `/${formValues.address.estado}` : ""} – CEP ${formValues.address.cep}`
                              : "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            {Number(cotacao.tro_codigo) > 1
                              ? "Coleta"
                              : "Recebimento"}
                          </p>
                          <p className="font-medium text-gray-800">
                            {cotacao.tro_codigo === "2"
                              ? cotacao.cve_coleta_cep
                                ? `${cotacao.cve_coleta_cep}, ${cotacao.cve_coleta_endereco}${cotacao.cve_coleta_numero ? `, ${cotacao.cve_coleta_numero}` : ""}${cotacao.cve_coleta_bairro ? ` – ${cotacao.cve_coleta_bairro}` : ""}`
                                : "Informe o CEP"
                              : `${cotacao.bop_origem_descricao || ""}${cotacao.municipio_base_origem ? ` – ${cotacao.municipio_base_origem}` : ""}` ||
                                "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            {Number(cotacao.trd_codigo) > 1
                              ? "Entrega"
                              : "Retirada"}
                          </p>
                          <p className="font-medium text-gray-800">
                            {cotacao.trd_codigo === "2"
                              ? cotacao.cve_entrega_cep
                                ? `${cotacao.cve_entrega_cep}, ${cotacao.cve_entrega_endereco}${cotacao.cve_entrega_numero ? `, ${cotacao.cve_entrega_numero}` : ""}${cotacao.cve_entrega_bairro ? ` – ${cotacao.cve_entrega_bairro}` : ""}`
                                : "Informe o CEP"
                              : `${cotacao.bop_destino_descricao || ""}${cotacao.municipio_base_destino ? ` – ${cotacao.municipio_base_destino}` : ""}` ||
                                "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Veículo
                          </p>
                          <p className="font-medium text-gray-800">
                            {cotacao.vmd_descricao || cotacao.cve_placa || "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Valor do serviço
                          </p>
                          <p className="font-medium text-gray-800">
                            {formatMoneyBr(quotePrice.total || 0)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {contractLoading ? (
                      (() => {
                        const STEPS = [
                          "Cadastrando cliente...",
                          "Preparando contrato...",
                          "Gerando PDF...",
                          "Carregando documento...",
                        ];
                        const currentIdx = STEPS.indexOf(contractLoadingStep);
                        return (
                          <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 space-y-4">
                            <p className="text-sm font-semibold text-n-primary">
                              Aguarde, gerando seu contrato...
                            </p>
                            <div className="space-y-2 pl-1">
                              {STEPS.map((step, i) => {
                                const done = i < currentIdx;
                                const active = i === currentIdx;
                                return (
                                  <div
                                    key={step}
                                    className={`flex items-center gap-2.5 text-sm transition-colors ${
                                      done
                                        ? "text-n-green"
                                        : active
                                          ? "text-n-primary font-medium"
                                          : "text-gray-400"
                                    }`}
                                  >
                                    {done ? (
                                      <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                                    ) : active ? (
                                      <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                                    ) : (
                                      <Circle className="w-4 h-4 flex-shrink-0" />
                                    )}
                                    {step}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()
                    ) : !contractGenerated ? (
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          className="flex items-center gap-2"
                          onClick={handleGenerateContract}
                        >
                          <FileSignature className="w-4 h-4" />
                          Gerar Contrato
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="border rounded-xl overflow-hidden shadow-sm">
                          <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b">
                            <span className="text-sm font-medium text-gray-700">
                              Contrato de Prestação de Serviços
                            </span>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 gap-1.5 text-xs"
                                disabled={!contractPdfUrl}
                                onClick={() =>
                                  openInNewTab(
                                    contractPdfUrl!,
                                    "Não foi possível abrir o contrato.",
                                  )
                                }
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                                Abrir em nova aba
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 gap-1.5 text-xs"
                                disabled={!contractPdfUrl}
                                onClick={async () => {
                                  if (!contractPdfUrl) return;
                                  try {
                                    const res = await fetch(contractPdfUrl);
                                    const blob = await res.blob();
                                    const blobUrl = URL.createObjectURL(blob);
                                    const a = document.createElement("a");
                                    a.href = blobUrl;
                                    a.download = `contrato_${cotacao.ctc_codigo}.pdf`;
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                    URL.revokeObjectURL(blobUrl);
                                  } catch {
                                    openInNewTab(
                                      contractPdfUrl,
                                      "Não foi possível baixar o contrato.",
                                    );
                                  }
                                }}
                              >
                                <Download className="w-3.5 h-3.5" />
                                Baixar PDF
                              </Button>
                            </div>
                          </div>
                          {contractPdfUrl ? (
                            <iframe
                              src={contractPdfUrl}
                              className="w-full h-[500px] bg-gray-50"
                              title="Contrato de prestação de serviços"
                            />
                          ) : (
                            <div className="flex items-center justify-center h-[500px] bg-gray-50 text-sm text-gray-500">
                              Contrato indisponível para visualização.
                            </div>
                          )}
                        </div>

                        {/* Ações após visualização */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-1">
                          {!contractSigned && (
                            <Button
                              type="button"
                              className="flex items-center gap-2 text-sm"
                              onClick={handleRegenerate}
                            >
                              <RefreshCw className="w-4 h-4" />
                              Gerar novamente
                            </Button>
                          )}

                          <div className="flex items-center gap-3">
                            <Checkbox
                              id="contract-accepted"
                              checked={contractAccepted}
                              disabled={contractSigned}
                              onCheckedChange={async (checked) => {
                                if (checked !== true) return;
                                setContractAccepted(true);
                                setContractSigned(true);
                                // Fire-and-forget: API não retorna dados, sempre considerar sucesso
                                registrarContratoAssinado(cotacao.ctc_codigo);
                                toast({
                                  title: "Contrato assinado com sucesso!",
                                  description:
                                    "Você será redirecionado para o pagamento em instantes.",
                                });
                                // Re-busca a cotação para confirmar a data de assinatura
                                await fetchCotacaoById(cotacao.cve_codigo);
                                setTimeout(() => {
                                  saveClientInfo(contractSnapshot!);
                                  onNext();
                                }, 3000);
                              }}
                            />
                            <Label
                              htmlFor="contract-accepted"
                              className="text-sm cursor-pointer leading-snug"
                            >
                              Li e aceito os termos do contrato acima
                            </Label>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Botões de navegação */}
              <div className="flex justify-end mt-6 gap-4">
                <Button type="button" variant="border" onClick={onBack}>
                  <ArrowLeft />
                  Voltar
                </Button>

                {alreadySigned ? (
                  <Button type="button" onClick={handleGoToPaymentSigned}>
                    Ir para pagamento
                    <ArrowRight />
                  </Button>
                ) : (
                  <Button type="submit">
                    Ir para pagamento
                    <ArrowRight />
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </div>

        {/* Coluna lateral — apenas desktop */}
        {!isMobile && (
          <div className="md:col-span-1">
            <CardContactSpecialist />
            <div className="mt-5 sticky top-32">
              <QuoteCardResume />
            </div>
          </div>
        )}
      </div>

      {/* Barra inferior mobile */}
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 rounded-t-2xl bg-white border-t shadow-lg z-50">
          <div
            className="p-4 cursor-pointer"
            onClick={() => setIsOrderSummaryExpanded(!isOrderSummaryExpanded)}
          >
            <div className="flex items-center justify-between pb-3 border-b">
              <p className="text-lg text-n-text-green font-bold">Resumo:</p>
              {isOrderSummaryExpanded ? (
                <ChevronDown className="h-5 w-5 text-n-green" />
              ) : (
                <ChevronUp className="h-5 w-5 text-n-green" />
              )}
            </div>
            <div className="flex items-center justify-between mt-2">
              <div>
                <p className="text-sm text-n-text">Total</p>
                <p className="text-xl font-bold text-n-green">
                  {formatMoneyBr(quotePrice.total || 0)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (alreadySigned) {
                      handleGoToPaymentSigned();
                    } else {
                      form.handleSubmit(handleSubmit)();
                    }
                  }}
                >
                  Avançar
                  <ArrowRight className="ml-2 h-[16px] w-[16px]" />
                </Button>
              </div>
            </div>
          </div>

          {isOrderSummaryExpanded && (
            <div className="border-t bg-gray-50 max-h-96 overflow-y-auto">
              <QuoteCardResumeMobile />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
